/*
 This file is part of GNU Taler
 (C) 2022-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import {
  Amounts,
  HttpStatusCode,
  Paytos,
  TalerError,
  assertUnreachable,
} from "@gnu-taler/taler-util";
import {
  revalidateAccountDetails,
  useAccountDetails,
} from "../../hooks/account.js";
import { Props, State } from "./index.js";
import { useTranslationContext } from "@gnu-taler/web-util/browser";

export function useComponentState({
  account,
  routeChargeWallet,
  routeOperationDetails,
  routeWireTransfer,
  routeCashout,
  routeTransactions,
  routeAccountDetails,
}: Props): State {
  const result = useAccountDetails(account);
  const { i18n } = useTranslationContext();
  if (!result) {
    return {
      status: "loading",
      error: undefined,
    };
  }

  if (result instanceof TalerError) {
    return {
      status: "loading-error",
      error: result,
      title: i18n.str`Failed to load account details.`,
      onRetry: () => void revalidateAccountDetails(),
    };
  }

  if (result.type === "fail") {
    switch (result.case) {
      case HttpStatusCode.Unauthorized:
        return {
          status: "login",

          reason: "forbidden",
        };
      case HttpStatusCode.NotFound:
        return {
          status: "login",

          reason: "not-found",
        };
      default: {
        assertUnreachable(result);
      }
    }
  }

  const { body: data } = result;

  const balanceIsDebit = data.balance.credit_debit_indicator == "debit";
  const parsedPayto = Paytos.fromString(data.payto_uri);
  const payto = parsedPayto.tag === "ok" ? parsedPayto.value : undefined;

  return {
    status: "ready",
    error: undefined,
    routeCashout,
    routeOperationDetails,
    routeChargeWallet,
    routeWireTransfer,
    routeTransactions,
    routeAccountDetails,
    account,
    accountPaytoUri: data.payto_uri,
    accountLegalName: Paytos.getAccountHolder(payto) ?? data.name,
    accountLoginName: Paytos.getAccountNumber(payto) ?? account,
    accountBankHost: Paytos.getBankHost(payto),
    balance: Amounts.parseOrThrow(data.balance.amount),
    balanceIsDebit,
  };
}
